import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Calendar, LogOut, Plus, Star, User, Menu, X, Clock, ArrowRight, Download, Zap, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { generateReceipt } from '../utils/receipt';
import { SEO } from '../components/SEO';
import axios from 'axios';

import { API_URL } from '../utils/api';

function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedBooking, setExpandedBooking] = useState(null);

  useEffect(() => {
    // Reconcile any pending/expired payments with Cashfree FIRST, then load bookings
    const init = async () => {
      try {
        await axios.post(`${API_URL}/api/bookings/reconcile`, {}, { withCredentials: true });
      } catch (e) {
        // Non-fatal — still load bookings even if reconcile fails
        console.warn('Reconcile skipped:', e?.message);
      }
      fetchBookings();
    };
    init();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bookings/user`, {
        withCredentials: true
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatSlot = (slot) => {
    const hour = parseInt(slot.split(':')[0]);
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const display = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${display}:00 ${suffix}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title="My Dashboard"
        description="View and manage your Malda Turf bookings."
        path="/dashboard"
        noindex
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <Trophy className="h-7 w-7 text-[#16A34A]" />
              <div>
                <span className="text-xl font-bold tracking-tight">Malda Turf</span>
                <p className="text-[10px] text-gray-500 tracking-widest uppercase">Premium Sports Booking</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-6">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#171717] border border-[#1A1A1A]" style={{ borderRadius: '6px' }}>
                <div className="w-7 h-7 bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center" style={{ borderRadius: '4px' }}>
                  <span className="text-[#16A34A] text-xs font-bold">{user?.name?.charAt(0) || 'U'}</span>
                </div>
                <span className="text-sm text-gray-300 font-body">{user?.name || 'User'}</span>
              </div>
              {user?.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  data-testid="admin-link"
                  className="text-sm text-gray-400 hover:text-[#16A34A] transition-colors font-body"
                >
                  Admin Panel
                </button>
              )}
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1.5"
              >
                <LogOut className="h-3.5 w-3.5" />
                Logout
              </button>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-[#1A1A1A] space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center" style={{ borderRadius: '4px' }}>
                  <span className="text-[#16A34A] text-xs font-bold">{user?.name?.charAt(0) || 'U'}</span>
                </div>
                <span className="text-sm text-gray-300">{user?.name}</span>
              </div>
              {user?.role === 'admin' && (
                <button onClick={() => navigate('/admin')} className="block text-sm text-gray-400">Admin Panel</button>
              )}
              <button onClick={handleLogout} className="btn-secondary w-full text-sm flex items-center justify-center gap-2">
                <LogOut className="h-3.5 w-3.5" /> Logout
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-24 pb-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Welcome Header */}
          <div className="mb-10">
            <p className="text-sm text-[#16A34A] uppercase tracking-widest mb-2 font-body">Dashboard</p>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">
              Welcome back, <span className="text-[#16A34A]">{user?.name?.split(' ')[0] || 'Player'}</span>
            </h1>
            <p className="text-gray-500 font-body text-sm">Manage your bookings and experiences</p>
          </div>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-2 gap-4 mb-12">
            <button
              onClick={() => navigate('/booking')}
              data-testid="new-booking-button"
              className="group bg-[#16A34A] text-[#0A0A0A] p-6 flex items-center justify-between transition-all hover:bg-[#00C853]"
              style={{ borderRadius: '8px' }}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0A0A0A]/10 flex items-center justify-center" style={{ borderRadius: '6px' }}>
                  <Plus className="h-6 w-6" />
                </div>
                <div className="text-left">
                  <div className="font-bold text-lg">New Booking</div>
                  <div className="text-sm opacity-70 font-body">Reserve your slot now</div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>

            <a
              href="https://maps.app.goo.gl/o8TmXUsUKnzYsDcbA"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="write-review-button"
              className="group bg-[#171717] border border-[#1A1A1A] text-white p-6 flex items-center justify-between transition-all hover:border-[#16A34A]"
              style={{ borderRadius: '8px' }}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center" style={{ borderRadius: '6px' }}>
                  <Star className="h-6 w-6 text-[#16A34A]" />
                </div>
                <div className="text-left">
                  <div className="font-bold text-lg">Write a Review</div>
                  <div className="text-sm text-gray-400 font-body">Rate us on Google Maps</div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-gray-500 transition-transform group-hover:translate-x-1 group-hover:text-[#16A34A]" />
            </a>
          </div>

          {/* Bookings Section */}
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold">My Bookings</h2>
              {bookings.length > 0 && (
                <p className="text-xs text-gray-500 font-body mt-1">
                  {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
            
            {loading ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#16A34A] mx-auto mb-4"></div>
                <p className="text-gray-500 text-sm font-body">Loading bookings...</p>
              </div>
            ) : bookings.length === 0 ? (
              <div className="bg-[#171717] border border-[#1A1A1A] p-12 text-center" style={{ borderRadius: '8px' }} data-testid="no-bookings-message">
                <div className="w-16 h-16 bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '8px' }}>
                  <Calendar className="h-8 w-8 text-[#16A34A]" />
                </div>
                <h3 className="text-xl font-bold mb-2">No Bookings Yet</h3>
                <p className="text-gray-500 font-body text-sm mb-6 max-w-sm mx-auto">
                  Book your first slot and experience premium turf facilities
                </p>
                <button
                  onClick={() => navigate('/booking')}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Book Now <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {bookings.map((booking) => {
                  const isExpanded = expandedBooking === booking.booking_id;
                  const HAPPY_SET = new Set(['03:00','04:00','05:00','06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00']);
                  const slots = booking.time_slots || [];
                  const happyCount = slots.filter(s => HAPPY_SET.has(s)).length;
                  const rushCount = slots.filter(s => !HAPPY_SET.has(s)).length;
                  const StatusIcon = booking.status === 'confirmed' ? Clock : Clock;

                  return (
                    <div
                      key={booking.booking_id}
                      className={`bg-[#171717] border transition-colors overflow-hidden ${
                        isExpanded ? 'border-[#262626]' : 'border-[#1A1A1A]'
                      }`}
                      style={{ borderRadius: '8px' }}
                      data-testid={`booking-card-${booking.booking_id}`}
                    >
                      {/* Main Row — Clickable */}
                      <button
                        onClick={() => setExpandedBooking(isExpanded ? null : booking.booking_id)}
                        className="w-full p-4 sm:p-5 text-left"
                        data-testid={`booking-toggle-${booking.booking_id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 flex items-center justify-center flex-shrink-0 ${
                              booking.status === 'confirmed' ? 'bg-[#16A34A]/10 border border-[#16A34A]/20' :
                              booking.status === 'pending' ? 'bg-yellow-500/10 border border-yellow-500/20' :
                              'bg-gray-500/10 border border-gray-500/20'
                            }`} style={{ borderRadius: '6px' }}>
                              <Calendar className={`h-4 w-4 ${
                                booking.status === 'confirmed' ? 'text-[#16A34A]' :
                                booking.status === 'pending' ? 'text-yellow-500' :
                                'text-gray-500'
                              }`} />
                            </div>
                            <div>
                              <span className="font-semibold text-sm block">{booking.booking_date}</span>
                              <span className="text-[11px] text-gray-500 font-body">
                                {happyCount > 0 && `Happy Hours × ${happyCount}`}
                                {happyCount > 0 && rushCount > 0 && ' · '}
                                {rushCount > 0 && `Rush Hours × ${rushCount}`}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${
                              booking.status === 'confirmed' ? 'border-[#16A34A]/30 text-[#16A34A] bg-[#16A34A]/5' :
                              booking.status === 'pending' ? 'border-yellow-500/30 text-yellow-500 bg-yellow-500/5' :
                              'border-gray-500/30 text-gray-500 bg-gray-500/5'
                            }`} style={{ borderRadius: '4px' }}>
                              {booking.status}
                            </div>
                            {isExpanded
                              ? <ChevronUp className="h-4 w-4 text-gray-500 flex-shrink-0" />
                              : <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            }
                          </div>
                        </div>
                      </button>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="border-t border-[#1A1A1A] px-5 py-5" data-testid={`booking-details-${booking.booking_id}`}>
                          <div className="grid sm:grid-cols-3 gap-4 mb-5">
                            <div>
                              <div className="text-[10px] text-gray-500 uppercase tracking-wider font-body mb-1">Booking ID</div>
                              <div className="text-xs font-mono text-gray-300">{booking.booking_id}</div>
                            </div>
                            <div>
                              <div className="text-[10px] text-gray-500 uppercase tracking-wider font-body mb-1">Duration</div>
                              <div className="text-sm font-semibold">{booking.total_hours || booking.time_slots?.length || 0} hour{(booking.total_hours || booking.time_slots?.length || 0) !== 1 ? 's' : ''}</div>
                            </div>
                            <div>
                              <div className="text-[10px] text-gray-500 uppercase tracking-wider font-body mb-1">Customer</div>
                              <div className="text-sm">{booking.customer_name}</div>
                              <div className="text-xs text-gray-500 font-body">{booking.customer_phone}</div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex flex-wrap gap-3">
                            <button
                              onClick={() => generateReceipt(booking)}
                              data-testid={`download-receipt-${booking.booking_id}`}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-[#262626] text-sm text-white hover:border-[#16A34A] hover:text-[#16A34A] transition-colors"
                              style={{ borderRadius: '6px' }}
                            >
                              <Download className="h-4 w-4" />
                              Download Receipt
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

export default DashboardPage;
